import { getAxiosClient } from '~src/api';
import { postEncryptedGiottoApiPayload } from './promptEncryption';

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const ENDPOINT = '/generate-image/background';
const DEFAULT_INITIAL_POLL_DELAY_MS = 1000;
const DEFAULT_POLL_INTERVAL_MS = 1000;
const DEFAULT_MAX_POLL_ATTEMPTS = 300;

const isPendingGenerationError = (error) => {
  const status = error?.response?.status;

  return (
    status === 404 ||
    status === 202 ||
    status === 425 ||
    status === 429 ||
    (status >= 500 && status < 600)
  );
};

const pollForAiBackgroundResult = async ({
  httpClient,
  requestId,
  initialPollDelayMs,
  pollIntervalMs,
  maxAttempts,
}) => {
  if (initialPollDelayMs > 0) {
    await sleep(initialPollDelayMs);
  }

  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    try {
      const response = await httpClient.get(
        `${process.env.GIOTTO_API_URL}${ENDPOINT}?requestId=${requestId}`
      );
      return response.data;
    } catch (error) {
      if (!isPendingGenerationError(error) || attempt === maxAttempts) {
        throw error;
      }

      if (pollIntervalMs > 0) {
        await sleep(pollIntervalMs);
      }
    }
  }

  throw new Error('AI background polling exhausted');
};

/**
 * Genera uno sfondo da un prompt testuale tramite lambda-giotto-api
 * (modello fal.ai openai/gpt-image-2, text-to-image).
 *
 * Endpoint asincrono: POST per accodare, poi GET in polling fisso finché il
 * backend smette di rispondere pending e ritorna 200.
 *
 * @param {Object} params
 * @param {string} params.prompt  testo della richiesta.
 * @param {number} [params.width]  larghezza in px (dal formato del progetto).
 * @param {number} [params.height] altezza in px. Il backend normalizza ai
 *   vincoli fal; se omessi usa la dimensione di default del modello.
 * @param {('auto'|'low'|'medium'|'high')} [params.quality='high']
 * @param {('jpeg'|'png'|'webp')} [params.outputFormat='png']
 * @param {number} [params.initialPollDelayMs=1000]
 * @param {number} [params.pollIntervalMs=1000]
 * @param {number} [params.maxAttempts=300]
 * @returns {Promise<{imageUrl: string, contentType?: string}>} dati dello
 *   sfondo generato.
 */
export default async function generateAiBackground({
  prompt,
  width,
  height,
  quality = 'high',
  outputFormat = 'png',
  initialPollDelayMs = DEFAULT_INITIAL_POLL_DELAY_MS,
  pollIntervalMs = DEFAULT_POLL_INTERVAL_MS,
  maxAttempts = DEFAULT_MAX_POLL_ATTEMPTS,
}) {
  const payload = { prompt, quality, outputFormat };
  if (width != null && height != null) {
    payload.width = width;
    payload.height = height;
  }
  const httpClient = await getAxiosClient();

  const queueResponse = await postEncryptedGiottoApiPayload(
    httpClient,
    `${process.env.GIOTTO_API_URL}${ENDPOINT}`,
    payload
  );

  const { requestId } = queueResponse.data;
  if (!requestId) {
    throw new Error('AI background queue response missing requestId');
  }

  return pollForAiBackgroundResult({
    httpClient,
    requestId,
    initialPollDelayMs,
    pollIntervalMs,
    maxAttempts,
  });
}
